import { gql } from '@apollo/client'

// Edit Template builder (branded reel). Queries the template's shot list + saved
// config, and saves / submits it.

export const GET_PROJECT_TEMPLATES = gql`
  query GetProjectTemplatesForReel($searchStr: String) {
    getProjectTemplates(searchStr: $searchStr) {
      rows {
        id
        name
      }
    }
  }
`

export const GET_SHOT_LIST = gql`
  query GetShotList($projectTemplateId: ID!) {
    getProjectTemplateSegmentSuggestions(projectTemplateId: $projectTemplateId) {
      rows {
        id
        name
        description
        suggestedSortOrder
        templateSegmentType
        projectTemplateSegmentClipSuggestions {
          shotStyle
          prompt
          teleprompterScript
        }
      }
    }
  }
`

export const GET_EDIT_TEMPLATE_CONFIG = gql`
  query GetEditTemplateConfig($projectTemplateId: ID!) {
    getEditTemplateConfig(projectTemplateId: $projectTemplateId) {
      id
      musicMode
      musicTrackId
      endingCardAssetId
      endingCardEnabled
      scenesConfig
    }
  }
`

// Submitted projects for the render dropdown — mirrors the Project Tracker's
// "Submitted" tab (getDescendentProjectsOfTeam filtered to SUBMITTED_STATUSES).
export const GET_SUBMITTED_PROJECTS = gql`
  query GetSubmittedProjectsForReel($teamId: ID!, $projectStatuses: [ProjectStatusOptions!]) {
    getDescendentProjectsOfTeam(
      teamId: $teamId
      onboardingProject: false
      projectStatuses: $projectStatuses
      limit: 200
      offset: 0
    ) {
      rows {
        id
        name
        projectStatus
        completedClipCount
        projectTemplate {
          id
          name
        }
        projectOwnerProfile {
          id
          firstName
          lastName
        }
      }
    }
  }
`

export const SAVE_EDIT_TEMPLATE_CONFIG = gql`
  mutation SaveEditTemplateConfig($input: SaveEditTemplateConfigInput!) {
    saveEditTemplateConfig(input: $input) {
      returnCode
      message
      id
    }
  }
`

export const SUBMIT_BRANDED_REEL = gql`
  mutation SubmitBrandedReel($input: SubmitBrandedReelInput!) {
    submitBrandedReel(input: $input) {
      id
      status
    }
  }
`

// Same input as a branded-reel render, but builds a Premiere/FCP7 export bundle (.zip) instead of a video.
export const SUBMIT_PREMIERE_EXPORT = gql`
  mutation SubmitPremiereExport($input: SubmitBrandedReelInput!) {
    submitPremiereExport(input: $input) {
      id
      status
    }
  }
`

// Polled by the floating render progress widget (RenderProgressWidget) until a job reaches a
// terminal status (completed/failed) — see DirectorJobStatusOptions in rolla-db's schema.
export const GET_DIRECTOR_JOB = gql`
  query GetDirectorJobForProgress($id: ID!) {
    getDirectorJob(id: $id) {
      id
      status
      percent
      stage
      error
      result {
        downloadUrl
      }
    }
  }
`

// Fetched once on load (useRehydrateRenderJobs) to re-seed the in-memory render-jobs store with
// the team's still-running jobs, so the floating widget survives a full page reload. directorVersion
// distinguishes a video render ('branded-reel-v0') from a Premiere export ('premiere-export-v0').
export const LIST_DIRECTOR_JOBS = gql`
  query ListDirectorJobsForProgress($teamId: ID, $statuses: [String!], $limit: Int) {
    listDirectorJobs(teamId: $teamId, statuses: $statuses, limit: $limit) {
      rows {
        id
        status
        percent
        stage
        error
        directorVersion
        createdAt
        project {
          id
          name
        }
      }
    }
  }
`

// Cancel an in-flight render/export from the progress widget. Terminal + user-initiated on the server:
// a late worker completion can no longer revive it.
export const CANCEL_DIRECTOR_JOB = gql`
  mutation CancelDirectorJob($id: ID!) {
    cancelDirectorJob(id: $id) {
      id
      status
    }
  }
`
